import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { taxApi } from '../utils/api';

// Wait this long after the last change before asking (Stripe bills each
// calculation; typing an address would otherwise ask once per key).
export const QUOTE_DEBOUNCE_MS = 400;

const SETTLED = new Set(['ok', 'none', 'pending', 'failed']);

/**
 * The request a form sends to POST /tax/quote. Only what the tax depends on,
 * so an unrelated edit (the card message) never asks again.
 */
export function quoteRequest({ items, deliveryFee, priorityTimeFee, fulfillmentType, recipient, exempt, orderNumber }) {
  const r = recipient || {};
  return {
    items: (items || []).map((item) => ({ price: item.price, quantity: item.quantity })),
    deliveryFee: Number(deliveryFee) || 0,
    priorityTimeFee: Number(priorityTimeFee) || 0,
    fulfillmentType: fulfillmentType === 'pickup' ? 'pickup' : 'delivery',
    recipient: fulfillmentType === 'pickup' ? {} : { address1: r.address1 || '', city: r.city || '', state: r.state || '', zip: r.zip || '' },
    exempt: exempt === true,
    ...(orderNumber ? { orderNumber } : {}),
  };
}

/**
 * Stripe Tax mode: an order form's tax comes from the server (POST
 * /tax/quote, a Stripe Tax calculation), never from the flat rate. Asks again
 * (debounced) whenever what the tax depends on changes.
 *
 * @param {object} input  quoteRequest's input
 * @param {{ enabled: boolean }} options  false (flat mode): never asks
 * @returns {{ status: 'idle'|'calculating'|'ok'|'none'|'pending'|'failed', tax: number|null, rate: number|null, message: string|null, settle: () => Promise<object> }}
 *   ok/none: tax and rate are known; pending: the server needs a delivery
 *   address first; failed: message says why. settle(): the answer for the
 *   current input (asks now if one is waiting), for a form about to save.
 */
export default function useStripeTaxQuote(input, { enabled }) {
  const body = useMemo(() => quoteRequest(input), [input]);
  const key = enabled ? JSON.stringify(body) : null;
  const [result, setResult] = useState({ key: null, status: 'idle', tax: null, rate: null, message: null });
  const keyRef = useRef(key);
  const bodyRef = useRef(body);
  const timerRef = useRef(null);
  const inflightRef = useRef(null); // { key, promise }
  const resultRef = useRef(result);
  keyRef.current = key;
  bodyRef.current = body;

  const run = useCallback(() => {
    const askedKey = keyRef.current;
    const promise = taxApi.quote(bodyRef.current)
      .then((res) => {
        const d = (res && res.data) || {};
        return {
          key: askedKey,
          status: SETTLED.has(d.status) ? d.status : 'failed',
          tax: typeof d.tax === 'number' ? d.tax : null,
          rate: typeof d.rate === 'number' ? d.rate : null,
          message: d.message || null,
        };
      })
      .catch((err) => ({ key: askedKey, status: 'failed', tax: null, rate: null, message: err?.message || 'Sales tax could not be calculated.' }))
      .then((answer) => {
        if (inflightRef.current && inflightRef.current.promise === promise) inflightRef.current = null;
        // Only the answer for what the form shows now counts.
        if (answer.key === keyRef.current) {
          resultRef.current = answer;
          setResult(answer);
        }
        return answer;
      });
    inflightRef.current = { key: askedKey, promise };
    return promise;
  }, []);

  useEffect(() => {
    if (!key) return undefined;
    if (resultRef.current.key === key) return undefined;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { run(); }, QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [key, run]);

  const settle = useCallback(async () => {
    if (!keyRef.current) return { status: 'idle', tax: null, rate: null, message: null };
    if (resultRef.current.key === keyRef.current) return resultRef.current;
    if (inflightRef.current && inflightRef.current.key === keyRef.current) return inflightRef.current.promise;
    clearTimeout(timerRef.current);
    return run();
  }, [run]);

  if (!key) return { status: 'idle', tax: null, rate: null, message: null, settle };
  if (result.key !== key) return { status: 'calculating', tax: null, rate: null, message: null, settle };
  return { status: result.status, tax: result.tax, rate: result.rate, message: result.message, settle };
}

/** The tax a quote charges now, or null while it is not known. */
export function quotedTax(quote, exempt) {
  if (exempt) return 0;
  return quote && (quote.status === 'ok' || quote.status === 'none') ? (quote.tax || 0) : null;
}
